import type {
  AgentWidgetConfig,
  Message,
  WidgetConfig,
  WidgetMode,
} from "../types";
import type { BridgeMessage, BridgeRequest, BridgeResponse } from "./types";

export enum CommandType {
  OPEN = "OPEN",
  CLOSE = "CLOSE",
  SEND_MESSAGE = "SEND_MESSAGE",
  INJECT_AGENT_MESSAGE = "INJECT_AGENT_MESSAGE",
  UPDATE_CONFIG = "UPDATE_CONFIG",
  SET_API_KEY = "SET_API_KEY",
  SET_INITIAL_CONFIG = "SET_INITIAL_CONFIG",
  SET_CUSTOM_DATA = "SET_CUSTOM_DATA",
  START_CONVERSATION = "START_CONVERSATION",
  GET_MESSAGES = "GET_MESSAGES",
  CLEAR_CONVERSATION = "CLEAR_CONVERSATION",
  END_CONVERSATION = "END_CONVERSATION",
  GET_STATE = "GET_STATE",
  PING = "PING",
}

export enum EventType {
  READY = "READY",
  OPENED = "OPENED",
  CLOSED = "CLOSED",
  MESSAGE_RECEIVED = "MESSAGE_RECEIVED",
  MESSAGE_SENT = "MESSAGE_SENT",
  CONVERSATION_STARTED = "CONVERSATION_STARTED",
  AI_STREAMING = "AI_STREAMING",
  AI_DONE = "AI_DONE",
  ERROR = "ERROR",
  CONFIG_LOADED = "CONFIG_LOADED",
  PONG = "PONG",
}

/**
 * Command types that can be sent to the widget
 */
export type Command =
  | { type: CommandType.OPEN }
  | { type: CommandType.CLOSE }
  | { type: CommandType.SEND_MESSAGE; payload: { message: string } }
  | { type: CommandType.INJECT_AGENT_MESSAGE; payload: { content: string } }
  | { type: CommandType.UPDATE_CONFIG; payload: Partial<WidgetConfig> }
  | { type: CommandType.SET_API_KEY; payload: string }
  | {
      type: CommandType.SET_INITIAL_CONFIG;
      payload: {
        apiKey: string;
        agentCustomData?: Record<string, unknown>;
        stream?: boolean;
        mode?: WidgetMode;
        overrides?: Partial<AgentWidgetConfig>;
      };
    }
  | { type: CommandType.SET_CUSTOM_DATA; payload: Record<string, unknown> }
  | { type: CommandType.START_CONVERSATION }
  | { type: CommandType.GET_MESSAGES }
  | { type: CommandType.CLEAR_CONVERSATION }
  | { type: CommandType.END_CONVERSATION }
  | { type: CommandType.GET_STATE }
  | { type: CommandType.PING };

/**
 * Event types that can be received from the widget
 */
export type Event =
  | { type: EventType.READY }
  | { type: EventType.OPENED }
  | { type: EventType.CLOSED }
  | { type: EventType.MESSAGE_RECEIVED; payload: { message: Message } }
  | { type: EventType.MESSAGE_SENT; payload: { message: string; timestamp: number } }
  | {
      type: EventType.CONVERSATION_STARTED;
      payload: { conversationId: string; isClosed?: boolean };
    }
  | { type: EventType.AI_STREAMING; payload: { token: string; isMcp?: boolean } }
  | { type: EventType.AI_DONE; payload: { message: Message } }
  | { type: EventType.ERROR; payload: { error: string; code?: string } }
  | { type: EventType.CONFIG_LOADED; payload: { primaryColor?: string; textColor?: string } }
  | { type: EventType.PONG };

const COMMANDS_EXPECTING_RESPONSE: ReadonlySet<CommandType> = new Set([
  CommandType.PING,
  CommandType.START_CONVERSATION,
  CommandType.GET_MESSAGES,
  CommandType.CLEAR_CONVERSATION,
  CommandType.END_CONVERSATION,
  CommandType.GET_STATE,
]);

export function createCommandMessage(command: Command): BridgeRequest {
  return {
    id: generateMessageId(),
    type: command.type,
    payload: "payload" in command ? command.payload : undefined,
    timestamp: Date.now(),
    expectsResponse: COMMANDS_EXPECTING_RESPONSE.has(command.type),
  };
}

export function createEventMessage(event: Event): BridgeMessage {
  return {
    id: generateMessageId(),
    type: event.type,
    payload: "payload" in event ? event.payload : undefined,
    timestamp: Date.now(),
  };
}

export function createResponseMessage(
  requestId: string,
  success: boolean,
  data?: unknown,
  error?: string
): BridgeResponse {
  return {
    id: generateMessageId(),
    type: "RESPONSE",
    payload: data,
    timestamp: Date.now(),
    requestId,
    success,
    error,
  };
}

const COMMAND_TYPE_VALUES = new Set<string>(Object.values(CommandType));
const EVENT_TYPE_VALUES = new Set<string>(Object.values(EventType));

export function isCommand(
  message: BridgeMessage
): message is BridgeRequest & { type: CommandType } {
  return COMMAND_TYPE_VALUES.has(message.type);
}

export function isEvent(
  message: BridgeMessage
): message is BridgeMessage & { type: EventType } {
  return EVENT_TYPE_VALUES.has(message.type);
}

export function isResponse(message: BridgeMessage): message is BridgeResponse {
  return message.type === "RESPONSE";
}

function generateMessageId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}
